import Chart from 'chart.js/auto';
import JSZip from 'jszip';
import { CM_COLORS, FREQUENCY_COLORS } from './analysis';

const BG = '#F8F9FA';
const TITLE = '#1D3557';
export const GRAPH_IDS = ['cm_key_metrics', 'cm_fault_category_distribution', 'cm_daily_work_orders', 'cm_top_10_locations', 'cm_fault_category_share', 'pm_key_metrics', 'pm_daily_work_orders', 'pm_frequency_breakdown'];
export const monthLabel = (month) => new Date(`${month}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const wrap = (value, limit = 34) => {
  const words = (value || '(Blank)').match(new RegExp(`.{1,${limit}}(?:\\s|$)|.{1,${limit}}`, 'g')) || ['(Blank)'];
  return words.map((w) => w.trim());
};

export function graphDefinitions(a) {
  return [
    { id: GRAPH_IDS[0], title: 'Corrective Maintenance - Key Metrics', type: 'metrics', values: a.cmMetrics, labels: ['Total CM Work Orders', 'Days of Activity', 'Unique Locations'], colors: ['#E63946', '#457B9D', '#2A9D8F'] },
    { id: GRAPH_IDS[1], title: 'CM Fault Category Distribution', type: 'horizontal', entries: a.categories, colors: a.categories.map(([k]) => CM_COLORS[k]) },
    { id: GRAPH_IDS[2], title: 'Daily CM Work Orders', type: 'daily', entries: a.cmDaily, colors: '#E63946' },
    { id: GRAPH_IDS[3], title: 'Top 10 CM Locations by Work Order Count', type: 'horizontal', entries: a.locations, colors: '#457B9D' },
    { id: GRAPH_IDS[4], title: 'CM Fault Category Share', type: 'pie', entries: a.categories, colors: a.categories.map(([k]) => CM_COLORS[k]) },
    { id: GRAPH_IDS[5], title: 'Preventive Maintenance - Key Metrics', type: 'metrics', values: a.pmMetrics, labels: ['Total PM Work Orders', 'Working Days', 'Unique Locations'], colors: ['#2A9D8F', '#457B9D', '#F4A261'] },
    { id: GRAPH_IDS[6], title: 'Daily PM Work Orders', type: 'daily', entries: a.pmDaily, colors: '#2A9D8F' },
    { id: GRAPH_IDS[7], title: 'PM Maintenance Frequency Breakdown', type: 'pie', entries: a.frequencies, colors: a.frequencies.map(([k]) => FREQUENCY_COLORS[k] || '#6C757D') },
  ].map((g) => ({ ...g, title: `${g.title} | ${monthLabel(a.month)}`, filename: `${a.month}_${g.id}.png` }));
}

function canvasBlob(canvas) {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('PNG export failed.')), 'image/png'));
}

export async function renderGraph(g) {
  const canvas = document.createElement('canvas');
  const width = g.type === 'daily' ? Math.max(1200, g.entries.length * 62) : 1200;
  const height = g.type === 'metrics' ? 300 : g.type === 'horizontal'
    ? Math.max(580, 140 + g.entries.reduce((sum, [label]) => sum + Math.max(42, wrap(label).length * 21 + 16), 0)) : 650;
  const scale = 3; // 3600px wide, suitable for a 12-inch image at 300 pixels/inch.
  canvas.width = width; canvas.height = height;
  let chart;
  try {
    if (g.type === 'metrics') {
      canvas.width *= scale; canvas.height *= scale;
      const ctx = canvas.getContext('2d'); ctx.scale(scale, scale);
      ctx.fillStyle = BG; ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = TITLE; ctx.textAlign = 'center'; ctx.font = 'bold 23px Arial'; ctx.fillText(g.title, width / 2, 38);
      g.values.forEach((value, i) => {
        const x = 24 + i * 392;
        ctx.fillStyle = g.colors[i]; ctx.fillRect(x, 68, 368, 208);
        ctx.fillStyle = '#FFFFFF'; ctx.font = 'bold 60px Arial'; ctx.fillText(String(value), x + 184, 166);
        ctx.font = '20px Arial'; ctx.fillText(g.labels[i], x + 184, 239);
      });
    } else {
      const horizontal = g.type === 'horizontal';
      const pie = g.type === 'pie';
      const entries = g.entries;
      const values = entries.map(([, n]) => n);
      const max = Math.max(...values, 1);
      const annotation = {
        id: 'maintenanceAnnotations',
        beforeDraw(c) { const { ctx } = c; ctx.save(); ctx.fillStyle = BG; ctx.fillRect(0, 0, c.width, c.height); ctx.restore(); },
        afterDatasetsDraw(c) {
          const { ctx } = c; ctx.save(); ctx.font = 'bold 16px Arial'; ctx.fillStyle = TITLE;
          if (!entries.length) { ctx.textAlign = 'center'; ctx.fillText('No valid dates available', c.width / 2, c.height / 2); }
          c.getDatasetMeta(0).data.forEach((element, i) => {
            if (pie) {
              const p = element.tooltipPosition(); const share = Math.round(values[i] / values.reduce((a, b) => a + b, 0) * 100);
              // Tiny slices are identified in the legend rather than overlapping percentage labels.
              if (share >= 3) { ctx.textAlign = 'center'; ctx.fillStyle = '#FFFFFF'; ctx.fillText(`${share}%`, p.x, p.y); }
            } else {
              ctx.textAlign = horizontal ? 'left' : 'center';
              ctx.fillText(String(values[i]), element.x + (horizontal ? 8 : 0), element.y + (horizontal ? 5 : -12));
            }
          }); ctx.restore();
        },
      };
      const labels = entries.map(([label, n]) => pie ? `${label || '(Blank)'} (${n}, ${Math.round(n / values.reduce((a, b) => a + b, 0) * 100)}%)` : horizontal ? wrap(label) : `${label.slice(8)} ${new Date(`${label}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}`);
      chart = new Chart(canvas, {
        type: pie ? 'pie' : 'bar',
        data: { labels, datasets: [
          { data: values, backgroundColor: g.colors, borderColor: '#FFFFFF', borderWidth: 2, ...(pie ? {} : { barPercentage: 0.58 }) },
          ...(g.type === 'daily' ? [{ type: 'line', data: values, borderColor: TITLE, backgroundColor: TITLE, borderWidth: 2, pointRadius: 4 }] : []),
        ] },
        plugins: [annotation],
        options: {
          responsive: false, animation: false, devicePixelRatio: scale, indexAxis: horizontal ? 'y' : 'x',
          layout: { padding: { top: 20, bottom: 24, left: 24, right: 60 } },
          plugins: { title: { display: true, text: g.title, color: TITLE, font: { size: 23, weight: 'bold' }, padding: { bottom: 30 } },
            legend: { display: pie, position: 'bottom', labels: { color: '#495057', font: { size: 16 }, padding: 18, boxWidth: 18 } }, tooltip: { enabled: false } },
          ...(pie ? { rotation: -50 } : { scales: {
            [horizontal ? 'x' : 'y']: { beginAtZero: true, suggestedMax: max * 1.15, ticks: { precision: 0, color: '#495057', font: { size: 16 } }, title: { display: true, text: 'Work Orders', font: { size: 17 } }, grid: { color: '#DEE2E6' } },
            [horizontal ? 'y' : 'x']: { grid: { display: false }, ticks: { autoSkip: false, minRotation: horizontal ? 0 : 30, maxRotation: horizontal ? 0 : 30, color: '#495057', font: { size: 16 } } },
          } }),
        },
      });
    }
    return { ...g, blob: await canvasBlob(canvas) };
  } finally { chart?.destroy(); canvas.width = 0; canvas.height = 0; }
}

export async function createGraphs(analysis) {
  const graphs = [];
  for (const definition of graphDefinitions(analysis)) {
    graphs.push(await renderGraph(definition));
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
  return graphs;
}

export async function createGraphZip(graphs, month, type = 'blob') {
  const expected = GRAPH_IDS.map((id) => `${month}_${id}.png`);
  if (graphs.length !== 8 || new Set(graphs.map((g) => g.filename)).size !== 8 || expected.some((name) => !graphs.some((g) => g.filename === name && g.blob))) throw new Error('All eight graphs must be ready before exporting.');
  const zip = new JSZip();
  graphs.forEach((g) => zip.file(g.filename, g.blob));
  return { filename: `RGA_Maintenance_Analysis_${month}.zip`, blob: await zip.generateAsync({ type }) };
}
